import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, ShieldAlert, Loader2, CheckCircle2, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface BleedItem { client_id: string; client_name: string; current_insurer_id: string; correct_insurer_id: string; via_job: string; sites: number; results: number }
interface ScanResult { mis_filed_clients: number; items: BleedItem[] }

interface DatasetItem { job_id: string; job_exists: boolean; orphaned: boolean; insurers: string[]; client_names: string[]; client_ids: string[]; sites: number; instructions: number; results: number; covers_blocking: number }
interface DatasetScan { datasets: number; items: DatasetItem[] }

interface OrphanSample { client_name?: string; site_reference?: string; insurer_id?: string; status?: string }
interface OrphanScan { orphan_site_ids: number; survey_instructions: number; survey_results: number; risk_assessments: number; insurers: string[]; samples: OrphanSample[]; total: number }

export const MasterSettings: React.FC = () => {
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [busy, setBusy] = useState<'scan' | 'fix' | null>(null);
  const [dsScan, setDsScan] = useState<DatasetScan | null>(null);
  const [dsBusy, setDsBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [orphanTenant, setOrphanTenant] = useState('');
  const [orphanScan, setOrphanScan] = useState<OrphanScan | null>(null);
  const [orphanBusy, setOrphanBusy] = useState<'scan' | 'purge' | null>(null);

  const runOrphanScan = async (): Promise<void> => {
    setOrphanBusy('scan');
    try {
      const q = orphanTenant.trim() ? `?insurer_id=${encodeURIComponent(orphanTenant.trim())}` : '';
      const r = await api.get<OrphanScan>(`/master/orphans/scan${q}`);
      setOrphanScan(r.data);
      toast.success(`Found ${r.data.total} orphaned record(s)`);
    } catch { toast.error('Orphan scan failed'); } finally { setOrphanBusy(null); }
  };

  const purgeOrphans = async (): Promise<void> => {
    if (!orphanScan || orphanScan.total === 0) return;
    const scope = orphanTenant.trim() ? `tenant ${orphanTenant.trim()}` : 'ALL tenants';
    if (!window.confirm(`Permanently delete ${orphanScan.total} orphaned record(s) across ${scope}?\n\nInstructions: ${orphanScan.survey_instructions}\nResults: ${orphanScan.survey_results}\nRisk ratings: ${orphanScan.risk_assessments}\n\nThese reference sites that no longer exist. This cannot be undone.`)) return;
    setOrphanBusy('purge');
    try {
      const body: { insurer_id?: string; confirm: boolean } = { confirm: true };
      if (orphanTenant.trim()) body.insurer_id = orphanTenant.trim();
      const r = await api.post<{ deleted: Record<string, number> }>('/master/orphans/purge', body);
      const d = r.data.deleted;
      toast.success(`Deleted ${d.survey_instructions} instructions, ${d.survey_results} results, ${d.risk_assessments} ratings`);
      await runOrphanScan();
    } catch { toast.error('Orphan purge failed'); } finally { setOrphanBusy(null); }
  };

  const toggleSel = (jobId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };

  const runDsScan = async (): Promise<void> => {
    setDsBusy('scan');
    setSelected(new Set());
    try {
      const r = await api.get<DatasetScan>('/master/imported-datasets/scan');
      setDsScan(r.data);
      toast.success(`Found ${r.data.datasets} imported dataset(s)`);
    } catch { toast.error('Scan failed'); } finally { setDsBusy(null); }
  };

  const purgeOne = async (jobId: string): Promise<{ instructions: number; sites: number; clients: number }> => {
    const r = await api.post<{ deleted: Record<string, number> }>('/master/imported-datasets/purge', { job_id: jobId, confirm: true });
    const d = r.data.deleted;
    return { instructions: d.survey_instructions, sites: d.sites, clients: d.clients };
  };

  const purgeDs = async (it: DatasetItem): Promise<void> => {
    const label = it.client_names.join(', ') || it.job_id;
    if (!window.confirm(`Permanently delete this import?\n\nClient: ${label}\nTenants: ${it.insurers.join(', ')}\nSurveys: ${it.instructions}  Sites: ${it.sites}\n\nThis cannot be undone.${it.covers_blocking > 0 ? `\n\n${it.covers_blocking} site(s) are referenced by a Confirmation of Cover and will be kept.` : ''}`)) return;
    setDsBusy(it.job_id);
    try {
      const d = await purgeOne(it.job_id);
      toast.success(`Deleted ${d.instructions} surveys, ${d.sites} sites${d.clients ? `, ${d.clients} client` : ''}`);
      await runDsScan();
    } catch { toast.error('Delete failed'); } finally { setDsBusy(null); }
  };

  const purgeSelected = async (): Promise<void> => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} selected import(s) across all tenants?\n\nThis cannot be undone. Sites referenced by a cover are kept automatically.`)) return;
    setDsBusy('bulk');
    let surveys = 0, sites = 0;
    try {
      for (const jobId of selected) {
        const d = await purgeOne(jobId);
        surveys += d.instructions; sites += d.sites;
      }
      toast.success(`Deleted ${surveys} surveys and ${sites} sites across ${selected.size} import(s)`);
      await runDsScan();
    } catch { toast.error('Bulk delete failed — some imports may remain'); await runDsScan(); } finally { setDsBusy(null); }
  };

  const runScan = async (): Promise<void> => {
    setBusy('scan');
    try {
      const r = await api.get<ScanResult>('/master/tenant-bleed/scan');
      setScan(r.data);
      toast.success(`Scan complete — ${r.data.mis_filed_clients} mis-filed client(s) found`);
    } catch { toast.error('Scan failed'); } finally { setBusy(null); }
  };

  const runFix = async (): Promise<void> => {
    if (!window.confirm('Re-home all mis-filed clients and their records to the correct tenant? This changes data.')) return;
    setBusy('fix');
    try {
      const r = await api.post<{ re_homed: Record<string, number> }>('/master/tenant-bleed/fix', { confirm: true });
      const m = r.data.re_homed;
      toast.success(`Re-homed: ${m.clients} clients, ${m.sites} sites, ${m.results} results`);
      await runScan();
    } catch { toast.error('Re-home failed'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Platform settings</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Global switches affecting every insurer tenant. Tenant-specific configuration lives inside each insurer's own Settings hub.</p>
      </div>

      <Card className="border-amber-300 bg-amber-50/60" data-testid="tenant-bleed-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800"><ShieldAlert size={18} /> Data integrity — tenant isolation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm font-body text-raptor-navy/80">
          <p>Finds import-created clients (and their sites, covers, surveys and ratings) that were stamped with the wrong insurer and re-homes them to the tenant of their import job. Scan is read-only; re-home asks for confirmation.</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => void runScan()} disabled={busy !== null} data-testid="bleed-scan-btn">
              {busy === 'scan' ? <Loader2 size={16} className="mr-1 animate-spin" /> : null} Scan for mis-filed records
            </Button>
            {scan && scan.mis_filed_clients > 0 && (
              <Button variant="gradient" onClick={() => void runFix()} disabled={busy !== null} data-testid="bleed-fix-btn">
                {busy === 'fix' ? <Loader2 size={16} className="mr-1 animate-spin" /> : null} Re-home {scan.mis_filed_clients} client(s)
              </Button>
            )}
          </div>
          {scan && scan.mis_filed_clients === 0 && (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="bleed-clean"><CheckCircle2 size={16} /> No mis-filed records — tenant isolation is clean.</div>
          )}
          {scan && scan.mis_filed_clients > 0 && (
            <div className="overflow-x-auto rounded border border-amber-200 bg-white" data-testid="bleed-report">
              <table className="w-full text-xs"><thead className="bg-amber-100/60 text-left"><tr>
                <th className="px-3 py-2">Client</th><th className="px-3 py-2">Currently in</th><th className="px-3 py-2">Should be in</th><th className="px-3 py-2">Sites</th><th className="px-3 py-2">Surveys</th>
              </tr></thead><tbody>
                {scan.items.map((it) => (
                  <tr key={it.client_id} className="border-t border-amber-100">
                    <td className="px-3 py-1.5">{it.client_name} <span className="text-raptor-navy/40">({it.client_id})</span></td>
                    <td className="px-3 py-1.5 text-red-600">{it.current_insurer_id}</td>
                    <td className="px-3 py-1.5 text-emerald-700">{it.correct_insurer_id}</td>
                    <td className="px-3 py-1.5 font-tabular">{it.sites}</td>
                    <td className="px-3 py-1.5 font-tabular">{it.results}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-red-300 bg-red-50/50" data-testid="imported-datasets-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-800"><Trash2 size={18} /> Imported datasets — delete</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm font-body text-raptor-navy/80">
          <p>Lists every Excel import (grouped by import job, across all tenants) with its surveys and sites — including orphaned imports whose job was already rolled back. Deleting removes that import's surveys, results, ratings and sites everywhere. Sites referenced by a Confirmation of Cover are kept automatically.</p>
          <Button variant="outline" onClick={() => void runDsScan()} disabled={dsBusy !== null} data-testid="dataset-scan-btn">
            {dsBusy === 'scan' ? <Loader2 size={16} className="mr-1 animate-spin" /> : null} Scan imported datasets
          </Button>
          {dsScan && dsScan.datasets === 0 && (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="dataset-clean"><CheckCircle2 size={16} /> No Excel-imported datasets found.</div>
          )}
          {dsScan && dsScan.datasets > 0 && (
            <div className="space-y-2" data-testid="dataset-report">
              {selected.size > 0 && (
                <Button variant="destructive" size="sm" onClick={() => void purgeSelected()} disabled={dsBusy !== null} data-testid="dataset-delete-selected">
                  {dsBusy === 'bulk' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Delete {selected.size} selected
                </Button>
              )}
              <div className="overflow-x-auto rounded border border-red-200 bg-white">
                <table className="w-full text-xs"><thead className="bg-red-100/60 text-left"><tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">Client / Import</th><th className="px-3 py-2">Tenant(s)</th><th className="px-3 py-2">Surveys</th><th className="px-3 py-2">Sites</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
                </tr></thead><tbody>
                  {dsScan.items.map((it) => (
                    <tr key={it.job_id} className="border-t border-red-100" data-testid={`dataset-row-${it.job_id}`}>
                      <td className="px-3 py-1.5">
                        <input type="checkbox" checked={selected.has(it.job_id)} onChange={() => toggleSel(it.job_id)} data-testid={`dataset-check-${it.job_id}`} />
                      </td>
                      <td className="px-3 py-1.5">{it.client_names.join(', ') || '—'} <span className="text-raptor-navy/40">({it.job_id})</span></td>
                      <td className="px-3 py-1.5">{it.insurers.join(', ')}</td>
                      <td className="px-3 py-1.5 font-tabular">{it.instructions}</td>
                      <td className="px-3 py-1.5 font-tabular">{it.sites}{it.covers_blocking > 0 ? <span className="text-amber-600"> ({it.covers_blocking} kept)</span> : null}</td>
                      <td className="px-3 py-1.5">{it.orphaned ? <span className="text-amber-600">orphaned</span> : 'active'}</td>
                      <td className="px-3 py-1.5">
                        <Button variant="destructive" size="sm" onClick={() => void purgeDs(it)} disabled={dsBusy !== null} data-testid={`dataset-delete-${it.job_id}`}>
                          {dsBusy === it.job_id ? <Loader2 size={14} className="animate-spin" /> : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-orange-300 bg-orange-50/50" data-testid="orphans-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-800"><ShieldAlert size={18} /> Orphaned records — rolled-back imports</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm font-body text-raptor-navy/80">
          <p>After an import is rolled back, its <b>sites</b> are removed but any instructions, results or risk ratings left behind (pointing at a site that no longer exists) can linger in Instructions and Risk Ratings. This finds and removes those orphans. Leave the tenant box empty to sweep every tenant, or enter an insurer id (e.g. <b>INS-002</b>) to scope it. Scan is read-only; purge asks for confirmation.</p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={orphanTenant}
              onChange={(e) => setOrphanTenant(e.target.value)}
              placeholder="Tenant id (optional, e.g. INS-002)"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-64"
              data-testid="orphan-tenant-input"
            />
            <Button variant="outline" onClick={() => void runOrphanScan()} disabled={orphanBusy !== null} data-testid="orphan-scan-btn">
              {orphanBusy === 'scan' ? <Loader2 size={16} className="mr-1 animate-spin" /> : null} Scan for orphans
            </Button>
            {orphanScan && orphanScan.total > 0 && (
              <Button variant="destructive" onClick={() => void purgeOrphans()} disabled={orphanBusy !== null} data-testid="orphan-purge-btn">
                {orphanBusy === 'purge' ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Delete {orphanScan.total} orphan(s)
              </Button>
            )}
          </div>
          {orphanScan && orphanScan.total === 0 && (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="orphan-clean"><CheckCircle2 size={16} /> No orphaned records — every instruction and rating points to a live site.</div>
          )}
          {orphanScan && orphanScan.total > 0 && (
            <div className="space-y-2" data-testid="orphan-report">
              <div className="flex flex-wrap gap-4 text-xs">
                <span>Instructions: <b className="font-tabular">{orphanScan.survey_instructions}</b></span>
                <span>Results: <b className="font-tabular">{orphanScan.survey_results}</b></span>
                <span>Risk ratings: <b className="font-tabular">{orphanScan.risk_assessments}</b></span>
                <span>Dead site refs: <b className="font-tabular">{orphanScan.orphan_site_ids}</b></span>
                <span>Tenant(s): <b>{orphanScan.insurers.join(', ') || '—'}</b></span>
              </div>
              {orphanScan.samples.length > 0 && (
                <div className="overflow-x-auto rounded border border-orange-200 bg-white">
                  <table className="w-full text-xs"><thead className="bg-orange-100/60 text-left"><tr>
                    <th className="px-3 py-2">Client</th><th className="px-3 py-2">Site ref</th><th className="px-3 py-2">Tenant</th><th className="px-3 py-2">Status</th>
                  </tr></thead><tbody>
                    {orphanScan.samples.map((s, i) => (
                      <tr key={i} className="border-t border-orange-100">
                        <td className="px-3 py-1.5">{s.client_name || '—'}</td>
                        <td className="px-3 py-1.5">{s.site_reference || '—'}</td>
                        <td className="px-3 py-1.5">{s.insurer_id || '—'}</td>
                        <td className="px-3 py-1.5">{s.status || '—'}</td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-raptor-blue/30 bg-raptor-blue/5">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={20} className="text-raptor-blue mt-0.5" />
          <div className="text-sm font-body text-raptor-navy/80">
            Platform-wide settings appear here as they become available. For now, use each insurer's Settings hub (via <b>Switch into insurer</b>) to manage integrations, branding, users, and roles.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
